<script lang="ts">
  import { onDestroy } from "svelte";
  import ValInputField from "./ValInputField.svelte";
  import { Form, Validate } from "../lib/validation_next";
  import { type Writable, writable } from "svelte/store";

  type Item = string | { label: string; disabled: boolean | undefined };
  function isString(item: Item): item is string {
    return typeof item === "string";
  }
  function getLabel(item: Item): string {
    if (isString(item)) {
      return item;
    } else {
      return item.label;
    }
  }
  function getOptionalLabel(item?: Item): string | undefined {
    if (item) {
      return getLabel(item);
    } else {
      return undefined;
    }
  }
  function getDisabled(item: Item): boolean {
    if (isString(item)) {
      return false;
    } else {
      return item.disabled ?? false;
    }
  }

  export let title: string;
  export let name: string;
  export let customClass: string | undefined;
  export let customClassLabel: string | undefined = undefined;
  export let items: Item[] = [];
  export let selectedIdx: Writable<number | undefined>;
  export let selectedLabel: Writable<string | undefined> = writable(
    undefined,
  );
  export let reduceResultsOnTyping: boolean = true;
  export let disableInput: boolean | undefined = false;
  export let disabled: boolean | undefined = false;
  export let onKeyPressed: ((value: string) => void) | undefined =
    undefined;
  export let onEnterPressed: ((value: string) => void) | undefined =
    undefined;
  export let onClearClicked: (() => void) | undefined = undefined;
  export let changeValueOnSelect: boolean | undefined = true;

  let previousSelectedIdx: number | undefined;
  let fieldFilterText = Form.field(Validate.len(0, 256));
  let container: HTMLDivElement | null = null;
  let filteredItems: Item[] = items;
  let open = false;
  let elementId = "input-dropdown" +
    Math.random().toString(36).substr(2, 9);

  /**
   * Handle the item click event
   * @param idx The index of the item clicked
   */
  const onItemClicked = (idx: number, label: string) => {
    if (changeValueOnSelect) {
      fieldFilterText.input.set(label);
    }
    $selectedIdx = idx;
    $selectedLabel = label;
  };

  /**
   * Handle the click outside event
   * @param event The mouse event
   */
  const handleClickOutside = (event: MouseEvent) => {
    const node = event.target as Node;
    if (container && !container.contains(node)) {
      open = false;
    }
  };

  /**
   * Handle the key up event
   * @param ke The keyboard event
   */
  const handleKeyUp = (ke: KeyboardEvent) => {
    if (ke.code === "Escape") {
      console.debug("Escape key pressed");
      open = false;
    } else if (
      ke.code !== "Enter" &&
      $fieldFilterText.res.isOk() &&
      onKeyPressed
    ) {
      const inputValue = $fieldFilterText.res.value;
      onKeyPressed(inputValue);
    } else if (
      ke.code === "Enter" &&
      $fieldFilterText.res.isOk() &&
      onEnterPressed
    ) {
      const inputValue = $fieldFilterText.res.value;
      onEnterPressed(inputValue);
    } else {
      open = true;
    }
  };

  /*
   * Cleanup the event listeners
   */
  onDestroy(() => {
    document.removeEventListener("mousedown", handleClickOutside);
    document
      .getElementById(elementId)
      ?.removeEventListener("keyup", handleKeyUp);
  });

  /**
   * Handle the input field change event
   */
  $: {
    if (disableInput === true && $selectedIdx === undefined) {
      fieldFilterText.input.set("");
    }

    if (
      $selectedIdx !== undefined && $selectedIdx !== previousSelectedIdx
    ) {
      fieldFilterText.input.set(
        getOptionalLabel(items.at($selectedIdx)) ?? "",
      );
      previousSelectedIdx = $selectedIdx;
    }

    // Filter input values
    if ($fieldFilterText.res.isOk()) {
      // Determine if the filter should be applied
      const shouldFilter = reduceResultsOnTyping &&
        $fieldFilterText.res.value.trim().length > 0;

      // Extract the filter text once to avoid multiple accesses
      const filterText = shouldFilter
        ? $fieldFilterText.res.value.toLowerCase().trim()
        : "";

      // Apply filter based on the condition
      filteredItems = shouldFilter
        ? items.filter((item) =>
          getLabel(item).toLowerCase().includes(filterText)
        )
        : items;
    }

    // Add event listeners

    document.addEventListener("mousedown", handleClickOutside);
    document
      .getElementById(elementId)
      ?.addEventListener("keyup", handleKeyUp);
  }
</script>

<div
  id={elementId}
  class={`relative ${customClass ? " " + customClass : ""}`}
  bind:this={container}
  on:click={() => {
    if (disabled) {
      return;
    }
    open = !open;
  }}
  role={undefined}
  aria-label="Autocomplete search"
>
  <ValInputField
    {name}
    {disabled}
    field={fieldFilterText}
    placeholder={title}
    onEnter={() => {}}
    onEscape={() => {
      open = false;
    }}
    customClass="w-full"
    {customClassLabel}
    img={$fieldFilterText.res.isOk() && $fieldFilterText.res.value.length > 0
      ? "/icons/cross.svg"
      : "/icons/arrow-down.svg"}
    imgClicked={() => {
      fieldFilterText.input.set("");
      if (onClearClicked) {
        onClearClicked();
      }
    }}
  />
  <div class={`${open ? "" : "hidden"} input-dropdown`}>
    <ul
      class="max-h-[315px] scrollbar-hide overflow-y-scroll scrollbar"
      id="item-list"
    >
      {#each filteredItems as item, idx}
        <li role="option" aria-selected={$selectedIdx === idx}>
          <button
            disabled={getDisabled(item)}
            class={`p-3 block transition-all disabled:bg-grey-5 disabled:text-grey-40 hover:font-600 w-full text-left hover:bg-[#F0F8F9] dark:hover:bg-[#000000] ${
              $selectedIdx === idx
                ? "bg-[#F0F8F9] dark:bg-[#000000]"
                : ""
            }`}
            on:click|preventDefault={() => {
              onItemClicked(idx, getLabel(item));
            }}
          >
            {getLabel(item)}
          </button>
        </li>
      {/each}
    </ul>
  </div>
</div>

<style>
  /* Scrollbar fix for Firefox Browser */
  .scrollbar {
    scrollbar-width: 0px;
  }
  /* Scrollbar fix for Chrome, EDGE, Opera, Others */
  .scrollbar::-webkit-scrollbar {
    width: 0px;
  }
  .scrollbar::-webkit-scrollbar-track {
    background: #fff;
  }
  .scrollbar::-webkit-scrollbar-thumb {
    background: #fff;
  }
</style>
